// (c) chaehongee. Confidential and proprietary.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export const SESSION_TABS_STORAGE_KEY = 'knoll.sessionTabs.v1'

export interface SessionTabsPersistedStateV1 {
    version: 1
    openSessionIds: string[]
    activeSessionId: string | null
}

export interface SessionTabsState {
    openSessionIds: string[]
    activeSessionId: string | null
    unreadCountBySessionId: Record<string, number>
    freshSessionIds: Record<string, boolean>
}

const initialState: SessionTabsState = {
    openSessionIds: [],
    activeSessionId: null,
    unreadCountBySessionId: {},
    freshSessionIds: {},
}

function normalizeSessionId(sessionId: string): string {
    return String(sessionId || '').trim()
}

function dedupePreserveOrder(ids: string[]): string[] {
    const seen = new Set<string>()
    const out: string[] = []
    for (const raw of ids) {
        const id = normalizeSessionId(raw)
        if (!id) continue
        if (seen.has(id)) continue
        seen.add(id)
        out.push(id)
    }
    return out
}

const sessionTabsSlice = createSlice({
    name: 'sessionTabs',
    initialState,
    reducers: {
        restoreFromStorage: (state, action: PayloadAction<SessionTabsPersistedStateV1>) => {
            const payload = action.payload
            if (!payload || payload.version !== 1) return
            const openSessionIds = dedupePreserveOrder(payload.openSessionIds || [])
            const activeSessionId = payload.activeSessionId
                ? normalizeSessionId(payload.activeSessionId)
                : null
            state.openSessionIds = openSessionIds
            state.activeSessionId = activeSessionId && openSessionIds.includes(activeSessionId)
                ? activeSessionId
                : openSessionIds[0] ?? null
        },
        openTab: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            if (!state.openSessionIds.includes(id)) {
                state.openSessionIds.push(id)
            }
            state.activeSessionId = id
        },
        openTabAfter: (state, action: PayloadAction<{ sessionId: string; afterSessionId: string }>) => {
            const id = normalizeSessionId(action.payload.sessionId)
            const afterId = normalizeSessionId(action.payload.afterSessionId)
            if (!id) return
            if (state.openSessionIds.includes(id)) return
            const afterIdx = state.openSessionIds.indexOf(afterId)
            if (afterIdx !== -1) {
                state.openSessionIds.splice(afterIdx + 1, 0, id)
            } else {
                state.openSessionIds.push(id)
            }
            state.activeSessionId = id
        },
        setActiveTab: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            if (!state.openSessionIds.includes(id)) {
                state.openSessionIds.push(id)
            }
            state.activeSessionId = id
            state.unreadCountBySessionId[id] = 0
        },
        closeTab: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            const idx = state.openSessionIds.indexOf(id)
            if (idx === -1) return

            state.openSessionIds.splice(idx, 1)
            delete state.unreadCountBySessionId[id]
            delete state.freshSessionIds[id]

            if (state.activeSessionId === id) {
                const nextId = state.openSessionIds[idx] ?? state.openSessionIds[idx - 1] ?? null
                state.activeSessionId = nextId
                if (nextId) state.unreadCountBySessionId[nextId] = 0
            }
        },
        incrementUnread: (state, action: PayloadAction<{ sessionId: string; by?: number }>) => {
            const sessionId = normalizeSessionId(action.payload.sessionId)
            if (!sessionId) return
            if (state.activeSessionId === sessionId) return
            const by = typeof action.payload.by === 'number' ? action.payload.by : 1
            const prev = state.unreadCountBySessionId[sessionId] ?? 0
            state.unreadCountBySessionId[sessionId] = Math.max(0, prev + Math.max(1, by))
        },
        clearUnread: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            state.unreadCountBySessionId[id] = 0
        },
        markSessionFresh: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            state.freshSessionIds[id] = true
        },
        clearSessionFresh: (state, action: PayloadAction<string>) => {
            const id = normalizeSessionId(action.payload)
            if (!id) return
            delete state.freshSessionIds[id]
        },
        resetTabs: (state) => {
            state.openSessionIds = []
            state.activeSessionId = null
            state.unreadCountBySessionId = {}
            state.freshSessionIds = {}
        },
    },
})

export const {
    restoreFromStorage,
    openTab,
    openTabAfter,
    setActiveTab,
    closeTab,
    incrementUnread,
    clearUnread,
    markSessionFresh,
    clearSessionFresh,
    resetTabs,
} = sessionTabsSlice.actions

export default sessionTabsSlice.reducer

